import React from 'react';
import NavBar from '../components/navigation/NavBar';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useFAB } from '../contexts/FABContext';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import LoadingScreen from '../components/common/LoadingScreen';
import FloatingActionButton, { FABActions } from '../components/common/FloatingActionButton';
import NotificationCenter from '../components/common/NotificationCenter';
const MainLayout = () => {
  const { user, loading } = useAuth();
  const { language } = useLanguage();
  const { isSidebarCollapsed } = useSidebar();
  const { isFABVisible } = useFAB();
  const location = useLocation();
  
  // Check if current page needs special layout
  const isFlowBuilder = location.pathname === '/flow-builder';
  const isFormBuilderEditor = location.pathname === '/form-builder-editor';
  const isNotesPage = location.pathname === '/notes';
  const isGanttView = location.pathname === '/projects-gantt' || 
                      location.pathname === '/project-board' || 
                      location.pathname === '/projects';
  // New modern dashboard should not have the common header top paddings/margins
  const isModernDashboard = location.pathname === '/' || 
                            location.pathname === '/dashboard' || 
                            location.pathname === '/customer-portal' ||
                            location.pathname.startsWith('/customer-portal/forms/');

  if (loading) {
    return <LoadingScreen />;
  }
  if (!user) {
    return <Navigate to="/login" />;
  }

  // Redirect to onboarding if user has no organization and hasn't completed onboarding
  // (skip for super_admin who already has org, and customer role)
  if (
    user.role !== 'super_admin' && 
    user.role !== 'customer' && 
    !user.organizationId && 
    !user.onboardingCompleted &&
    location.pathname !== '/onboarding' &&
    location.pathname !== '/profile'
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  // Redirect customer role users to customer portal if they try to access dashboard
  if (user.role === 'customer' && (location.pathname === '/' || location.pathname === '/dashboard')) {
    return <Navigate to="/customer-portal" replace />;
  }

  // Get current page context for FAB actions
  const getCurrentPageActions = () => {
    const currentPath = location.pathname;
    
    // Base actions available on all pages
    const baseActions = [
      {
        ...FABActions.addCustomer,
        onClick: () => {
          window.location.href = '/customers';
        }
      },
      {
        ...FABActions.addProject,
        onClick: () => {
          window.location.href = '/projects';
        }
      },
      {
        ...FABActions.addTask,
        onClick: () => {
          window.location.href = '/tasks';
        }
      }
    ];

    // Page-specific actions
    if (currentPath === '/' || currentPath.includes('/deals')) {
      return [
        {
          ...FABActions.addDeal,
          onClick: () => {
            if (currentPath.includes('/deals')) {
              // If already on deals page, trigger form
              const event = new CustomEvent('openDealForm');
              window.dispatchEvent(event);
            } else {
              // Navigate to deals page
              window.location.href = '/deals';
            }
          }
        },
        ...baseActions
      ];
    }

    if (currentPath.includes('/customers')) {
      return [
        {
          ...FABActions.addCustomer,
          onClick: () => {
            // Trigger new customer form if on customers page
            const event = new CustomEvent('openCustomerForm');
            window.dispatchEvent(event);
          }
        },
        {
          ...FABActions.addDeal,
          onClick: () => {
            window.location.href = '/deals';
          }
        },
        {
          ...FABActions.addProject,
          onClick: () => {
            window.location.href = '/projects';
          }
        },
        {
          ...FABActions.addTask,
          onClick: () => {
            window.location.href = '/tasks';
          }
        }
      ];
    }

    if (currentPath.includes('/quotations')) {
      return [
        {
          ...FABActions.addDocument,
          label: 'Új árajánlat',
          onClick: () => {
            const event = new CustomEvent('openQuotationForm');
            window.dispatchEvent(event);
          }
        },
        ...baseActions
      ];
    }

    if (currentPath.includes('/orders')) {
      return [
        {
          ...FABActions.addDocument,
          label: 'Új megrendelés',
          onClick: () => {
            const event = new CustomEvent('openOrderForm');
            window.dispatchEvent(event);
          }
        },
        ...baseActions
      ];
    }

    if (currentPath.includes('/projects')) {
      return [
        {
          ...FABActions.addProject,
          onClick: () => {
            if (currentPath.includes('/projects')) {
              // If already on projects page, trigger form
              const event = new CustomEvent('openProjectForm');
              window.dispatchEvent(event);
            } else {
              // Navigate to projects page
              window.location.href = '/projects';
            }
          }
        },
        {
          ...FABActions.addTask,
          onClick: () => {
            window.location.href = '/tasks';
          }
        },
        {
          ...FABActions.addCustomer,
          onClick: () => {
            window.location.href = '/customers';
          }
        }
      ];
    }

    if (currentPath.includes('/tasks')) {
      return [
        {
          ...FABActions.addTask,
          onClick: () => {
            if (currentPath.includes('/tasks')) {
              // If already on tasks page, trigger form
              const event = new CustomEvent('openTaskForm');
              window.dispatchEvent(event);
            } else {
              // Navigate to tasks page
              window.location.href = '/tasks';
            }
          }
        },
        {
          ...FABActions.addProject,
          onClick: () => {
            window.location.href = '/projects';
          }
        },
        {
          ...FABActions.addCustomer,
          onClick: () => {
            window.location.href = '/customers';
          }
        }
      ];
    }

    if (currentPath.includes('/ai-quotations')) {
      // AI Quotations page has its own generate button, minimal FAB
      return [
        {
          ...FABActions.addCustomer,
          onClick: () => {
            window.location.href = '/customers';
          }
        }
      ];
    }

    if (currentPath.includes('/flow-builder')) {
      // Flow Builder page has its own node creation system, no FAB needed
      return [];
    }

    if (currentPath.includes('/form-builder-editor')) {
      // Form Builder Editor page has its own field creation system, no FAB needed
      return [];
    }

    if (currentPath.includes('/notes')) {
      // Notes page has its own note creation system, minimal FAB
      return [
        {
          ...FABActions.addCustomer,
          onClick: () => {
            window.location.href = '/customers';
          }
        }
      ];
    }

    // Default actions for other pages
    return baseActions;
  };

  return (
    <div className="flex h-screen bg-[var(--crm-bg)] overflow-hidden">
      <NavBar />

      {/* Main Content Area */}
      <div className={`flex-1 min-w-0 ${
        isModernDashboard ? 'pt-20 md:pt-0' : 'pt-20 md:pt-16'
      } flex flex-col transition-all duration-300 ${
        isSidebarCollapsed 
          ? 'md:ml-16' 
          : 'md:ml-48 lg:ml-64'
      }`}>
        {/* Content - Scrollable */}
        <main className={`flex-1 min-w-0 w-full overflow-x-hidden bg-[var(--crm-bg)] ${
          (isFlowBuilder || isFormBuilderEditor || isNotesPage)
            ? 'overflow-hidden' 
            : isGanttView
              ? 'overflow-auto'
              : isModernDashboard
                ? 'overflow-auto p-4'
                : 'overflow-auto p-4 sm:p-6 lg:p-8 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-200 dark:[&::-webkit-scrollbar-track]:bg-gray-700 [&::-webkit-scrollbar-thumb]:bg-gray-400 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-primary-500/50'
        }`}>
          <Outlet />
        </main>
      </div>

      {/* Global Floating Action Button */}
      {getCurrentPageActions().length > 0 && isFABVisible && (
        <FloatingActionButton
          actions={getCurrentPageActions()}
          position="bottom-right"
          size="md"
        />
      )}

      {/* Notification Center Sidebar */}
      <NotificationCenter />

    </div>
  );
};

export default MainLayout; 